import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listPatients, listRooms } from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';
import { StatusChip } from '../components/StatusChip';
import ResidentAvatar from '../components/ResidentAvatar';
import { MagnifyingGlass, ArrowRight, DoorOpen, Buildings } from '@phosphor-icons/react';

const FILTERS = ['all', 'critical', 'warning', 'ok'];

export default function Patients() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [roomBuilding, setRoomBuilding] = useState({}); // room id -> building name

  // Patients carry no building field, only assigned_room_id. Map room -> building
  // (corpus_name) once so we can show each patient's building.
  useEffect(() => {
    listRooms({ limit: 200, offset: 0 })
      .then(({ items }) => {
        const map = {};
        for (const r of items) if (r.id && r.corpus_name) map[r.id] = r.corpus_name;
        setRoomBuilding(map);
      })
      .catch(() => setRoomBuilding({}));
  }, []);

  useEffect(() => {
    setLoading(true);
    listPatients({ q: q || undefined, status })
      .then(({ items }) => setPatients(items))
      .finally(() => setLoading(false));
  }, [q, status]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (q) next.set('q', q); else next.delete('q');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [q]);

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
          {t('nav_patients')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{t('residents_title')}</h1>
        <p className="text-ink-soft mt-3 max-w-xl">{t('residents_sub')}</p>
      </header>

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            type="text"
            placeholder={t('search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="patients-search"
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all"
          />
        </div>
        <div className="flex gap-2 bg-white border border-stone_line rounded-xl p-1" data-testid="patients-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setStatus(f)}
              data-testid={`patients-filter-${f}`}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                status === f ? 'bg-sage text-white' : 'text-ink-soft hover:bg-bg-alt'
              }`}
            >
              {f === 'all' ? t('all') : t(`status_${f}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : patients.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">{t('no_results')}</div>
        ) : (
          <ul className="divide-y divide-stone_line" data-testid="patients-list">
            {patients.map((r) => (
              <li key={r.id} className="px-6 py-5 flex items-center gap-5 hover:bg-bg-alt/40 transition-colors">
                <ResidentAvatar name={r.name} src={r.avatar_url} size={48} testid={`patient-avatar-${r.id}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="font-medium text-ink truncate">{r.name}</div>
                    <StatusChip status={r.status} />
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2">
                  {r.room_id && r.room_label && r.room_label !== '—' && (
                    <Link
                      to={`/rooms/${encodeURIComponent(r.room_id)}`}
                      data-testid={`patient-room-${r.id}`}
                      title={t('room')}
                      className="inline-flex items-center gap-2 text-xs text-ink-soft hover:text-sage-700 px-3 py-1.5 rounded-lg hover:bg-sage-100 transition-colors"
                    >
                      <DoorOpen size={16} weight="duotone" />
                      {r.room_label}
                    </Link>
                  )}
                  {(roomBuilding[r.room_id] || r.corpus_name || r.building_name) && (
                    <span
                      data-testid={`patient-building-${r.id}`}
                      title={t('admin_field_building')}
                      className="inline-flex items-center gap-2 text-xs text-ink-soft px-3 py-1.5 rounded-lg bg-bg-alt"
                    >
                      <Buildings size={16} weight="duotone" />
                      {roomBuilding[r.room_id] || r.corpus_name || r.building_name}
                    </span>
                  )}
                </div>
                <Link
                  to={`/patients/${r.id}`}
                  data-testid={`patient-view-${r.id}`}
                  className="inline-flex items-center gap-2 text-sm text-sage hover:text-sage-600"
                >
                  {t('view_profile')} <ArrowRight size={16} weight="bold" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
